import { useEffect, useState, useContext, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Styles from './UserInternshipPackInfo.module.scss';
import CryptoJS from 'crypto-js';
import { Tooltip as ReactTooltip } from "react-tooltip";

// Module imports
import { LinearProgress, Tooltip, Typography, Dialog } from '@mui/material';
import { LightbulbOutlined, CheckCircleOutlined, WorkspacePremiumOutlined, MenuBookOutlined, SchoolOutlined, MenuBookRounded, DownloadOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';

// Component imports
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Notification } from 'components/notifications/notification';
import { AuthContext, ViewContext, Message } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';


export function UserInternshipPackInfo(props) {
    const { internship_id } = useParams();
    const navigate = useNavigate();
    const context = useContext(AuthContext);
    const [packData, setPackData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [labCerts, setLabCerts] = useState([]);
    const [showNotOnboardedDialog, setShowNotOnboardedDialog] = useState(false);

    const viewContext = useContext(ViewContext);

    const renewBtnRef = useRef(null);

    const STATUS_CHIP_CLASS_MAP = {
        'in_progress': 'inProgressChip',
        'not_started': 'notStartedChip',
        'completed': 'completedChip',
        'failed': 'failedChip',
        'expired': 'expiredChip',
    };

    const resolveStatusChipClass = (status, Styles) => {
        if (!status) return Styles.notStartedChip;
        return Styles[STATUS_CHIP_CLASS_MAP[status]] || Styles.notStartedChip;
    };

    // Format status for display
    const formatStatus = (status) => {
        if (!status) return 'Not Started';
        // Convert snake_case to Title Case
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const handleLabClick = (data, navigator, is_cert = 0, cert_id = false) => {
        const id = data.id;
        const labStatus = data.status;
        // Encrypt function to certificate id
        const encrypt = (text) => {
            return CryptoJS.AES.encrypt(text, 'secretKey').toString();
        };
        const options = {
            method: "POST",
            url: `/api/user/labs/${id}/assign/${is_cert}`,
            headers: {
                "accept": "application/json",
            },
        };

        if (labStatus === "not_started") {
            axios
                .request(options)
                .then(async function (response) {
                    cert_id && sessionStorage.setItem("certID", encrypt(cert_id));
                    await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                    navigator(`/labs/${id}/nodes`);
                })
                .catch((err) => {
                    let status = err.response.status;
                    console.log("error", status);
                    navigator('/oops');
                });
        } else {
            cert_id && sessionStorage.setItem("certID", encrypt(cert_id));
            navigator(`/labs/${id}/nodes`);
        }
    };

    useEffect(() => {
        const fetchPackData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/user/internship/${internship_id}/info`);
                setPackData(response.data);
            } catch (error) {
                console.error('Error fetching pack data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (internship_id) {
            fetchPackData();
        }
    }, [internship_id]);

    const scrollToTop = () => {
        // Scroll to top
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Shake renew button after scroll starts
        setTimeout(() => {
            if (renewBtnRef.current) {
                renewBtnRef.current.classList.add(Styles.shake);

                // remove shake class after animation
                setTimeout(() => {
                    renewBtnRef.current.classList.remove(Styles.shake);
                }, 800);
            }
        }, 500);

        return;
    }

    const labsCerts = (labID) => {
        if (isDisabled) {
            scrollToTop();
        }
        const postData = {
            labId: labID,
        };

        const url = '/api/labs/lpath/all';

        axios.post(url, postData)
            .then(function (response) {
                setLabCerts(response.data?.data)
            })
            .catch(function (error) {
                console.error('POST request failed', error);
            });
    }

    const handleNavigation = (type, id, data = null) => {
        if (isDisabled) {
            scrollToTop();
            return;
        }
        switch (type) {
            case 'certificate':
                navigate(`/lpaths/${id}/learningpath`);
                break;

            case 'course':
                handleLabClick(data, navigate);
                break;

            case 'project':
                handleLabClick(data, navigate);
                break;

            default:
                console.warn(`Unknown navigation type: ${type}`);
                break;
        }
    };

    const handleDownloadCertificate = async () => {
        if (!context?.user?.onboarded) {
            setShowNotOnboardedDialog(true);
            return;
        }
        if (!data?.completion_date) {
            console.warn("Certificate not available yet.");
            return;
        }
        navigate(`/internship/certificate/${data?.internship_cert_id}`);
    };

    const calculateProgress = () => {
        if (!packData?.data) return 0;

        const { data } = packData;
        const certifications = data.certifications || [];
        const labs = data.labs || [];
        const projects = data.projects || [];

        // If there's a completion_percentage at the root level, use it
        if (data.completion_percentage !== undefined && data.completion_percentage !== null) {
            return Math.round(data.completion_percentage);
        }

        // Otherwise calculate from all items
        const totalItems = certifications.length + labs.length + projects.length;

        if (totalItems === 0) return 0;

        const totalProgress = [
            ...certifications.map(cert => cert.completion_percentage || 0),
            ...labs.map(lab => lab.completion_percentage || 0),
            ...projects.map(proj => proj.completion_percentage || 0)
        ].reduce((sum, progress) => sum + progress, 0);

        return Math.round(totalProgress / totalItems);
    };

    const onClickRenew = () => {
        navigate(`/internships/info/${packData?.data?.id}`);
    }

    if (loading) {
        return <LoadingBackdrop isOpen={true} />;
    }

    const isDisabled = packData?.data.subscription_status === 'disabled' || packData?.data.subscription_status === 'expired';


    if (!packData?.data) {
        return (
            <div className={Styles.container} data-testid="userInternshipPackInfo-container">
                <p data-testid="userInternshipPackInfo-noData">{props.t?.('InternshipPack.UserInternshipPackInfo.noData')}</p>
            </div>
        );
    }

    const { data, labsCount, projectsCount, certificationsCount } = packData;
    const isCertificateDisabled = !data?.completion_date;

    return (
        <>
            <Notification call='internship' />
            <div className={Styles.container} data-testid="userInternshipPackInfo-container">
                {(data?.completion_date) &&
                    <Message
                        closable
                        title={props.t('InternshipPack.UserInternshipPackInfo.message.title')}
                        text={props.t('InternshipPack.UserInternshipPackInfo.message.descr')}
                        type="success"
                    />}
                <div className={Styles.breadcrumbSection} data-testid="userInternshipPackInfo-breadcrumbSection">
                    <BreadCrumbs
                        onClickPaths={['/internships', '']}
                        crumbs={[
                            props.t?.('InternshipPack.UserInternshipPackInfo.breadcrumbs.internships'),
                            data.name
                        ]}
                    />
                </div>
                {/* Hero Section */}
                <div className={Styles.heroSection} data-testid="userInternshipPackInfo-heroSection">
                    <div className={Styles.heroContent} data-testid="userInternshipPackInfo-heroContent">
                        <div className={Styles.topSection} data-testid="userInternshipPackInfo-topSection">
                            <div className={Styles.badgeAndButton} data-testid="userInternshipPackInfo-badgeAndButton">
                                <div className={Styles.iconWrapper} data-testid="userInternshipPackInfo-iconWrapper">
                                    <WorkspacePremiumOutlined className={Styles.badgeIcon} />
                                </div>

                                <span className={Styles.badge} data-testid="userInternshipPackInfo-badge">
                                    {props.t?.('InternshipPack.UserInternshipPackInfo.badge')}
                                </span>
                            </div>
                            <h1 className={Styles.title} data-testid="userInternshipPackInfo-title">{data.name}</h1>
                            <p className={Styles.description} data-testid="userInternshipPackInfo-description">{data.descr}</p>
                            <div className={Styles.tooltipAnchor}>
                                <Tooltip
                                    title={
                                        data?.completion_date ? (
                                            <Typography fontSize={16}>
                                                {props.t?.('InternshipPack.UserInternshipPackInfo.tooltips.completed')}
                                            </Typography>
                                        ) : (
                                            <Typography fontSize={16}>
                                                {props.t?.('InternshipPack.UserInternshipPackInfo.tooltips.notCompleted')}
                                            </Typography>
                                        )
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <span className={Styles.downloadWrapper}>
                                        <button
                                            id="downloadCertBtn"
                                            disabled={isCertificateDisabled}
                                            onClick={handleDownloadCertificate}
                                            className={Styles.downloadBtn}
                                        >
                                            <DownloadOutlined className={Styles.downloadIcon} />
                                            {props.t?.('InternshipPack.UserInternshipPackInfo.downloadBtn')}
                                        </button>
                                    </span>
                                </Tooltip>

                                {isDisabled && <button
                                    id="renewBtn"
                                    ref={renewBtnRef}
                                    onClick={onClickRenew}
                                    className={Styles.renewBtn}
                                >
                                    Renew Subscription
                                </button>}
                            </div>

                        </div>

                        <div className={Styles.statsGrid} data-testid="userInternshipPackInfo-statsGrid">
                            <div className={Styles.statCard} data-testid="userInternshipPackInfo-statCard">
                                <div className={Styles.statIconWrapper} data-testid="userInternshipPackInfo-statIconWrapper">
                                    <MenuBookOutlined className={Styles.statIcon} />
                                </div>
                                <div className={Styles.statValue} data-testid="userInternshipPackInfo-statValue">{labsCount}</div>
                                <div className={Styles.statLabel} data-testid="userInternshipPackInfo-statLabel">
                                    {props.t?.('InternshipPack.UserInternshipPackInfo.stats.courses')}
                                </div>
                            </div>

                            <div className={Styles.statCard} data-testid="userInternshipPackInfo-statCard">
                                <div className={Styles.statIconWrapper} data-testid="userInternshipPackInfo-statIconWrapper">
                                    <WorkspacePremiumOutlined className={Styles.statIcon} />
                                </div>
                                <div className={Styles.statValue} data-testid="userInternshipPackInfo-statValue">{certificationsCount}</div>
                                <div className={Styles.statLabel} data-testid="userInternshipPackInfo-statLabel">
                                    {props.t?.('InternshipPack.UserInternshipPackInfo.stats.certificates')}
                                </div>
                            </div>

                            <div className={Styles.statCard} data-testid="userInternshipPackInfo-statCard">
                                <div className={Styles.statIconWrapper} data-testid="userInternshipPackInfo-statIconWrapper">
                                    <LightbulbOutlined className={Styles.statIcon} />
                                </div>
                                <div className={Styles.statValue} data-testid="userInternshipPackInfo-statValue">{projectsCount}</div>
                                <div className={Styles.statLabel} data-testid="userInternshipPackInfo-statLabel">
                                    {props.t?.('InternshipPack.UserInternshipPackInfo.stats.projects')}
                                </div>
                            </div>

                            <div className={Styles.statCard} data-testid="userInternshipPackInfo-statCard">
                                <div className={Styles.statIconWrapper} data-testid="userInternshipPackInfo-statIconWrapper">
                                    <SchoolOutlined className={Styles.statIcon} />
                                </div>
                                <div className={Styles.statValue} data-testid="userInternshipPackInfo-statValue">{props.t?.('InternshipPack.UserInternshipPackInfo.stats.onlineCount')}</div>
                                <div className={Styles.statLabel} data-testid="userInternshipPackInfo-statLabel">
                                    {props.t?.('InternshipPack.UserInternshipPackInfo.sections.online')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={Styles.mainContent} data-testid="userInternshipPackInfo-mainContent">
                    {/* Progress Summary */}
                    <div className={Styles.progressSummaryWrapper} data-testid="userInternshipPackInfo-progressSummaryWrapper">
                        <div className={Styles.progressSummaryCard} data-testid="userInternshipPackInfo-progressSummaryCard">
                            <div className={Styles.progressSummaryHeader} data-testid="userInternshipPackInfo-progressSummaryHeader">
                                <div className={Styles.progressSummaryText} data-testid="userInternshipPackInfo-progressSummaryText">
                                    <h2 className={Styles.progressSummaryTitle} data-testid="userInternshipPackInfo-progressSummaryTitle">
                                        {props.t?.('InternshipPack.UserInternshipPackInfo.progress.title')}
                                    </h2>
                                    <p className={Styles.progressSummarySubtitle} data-testid="userInternshipPackInfo-progressSummarySubtitle">
                                        {props.t?.('InternshipPack.UserInternshipPackInfo.progress.subtitle')}
                                    </p>
                                </div>

                                <div className={Styles.progressSummaryNumbers} data-testid="userInternshipPackInfo-progressSummaryNumbers">
                                    <span className={Styles.progressSummaryPercent} data-testid="userInternshipPackInfo-progressSummaryPercent">
                                        {calculateProgress()}%
                                    </span>
                                </div>
                            </div>

                            <div className={Styles.progressSummaryBarWrapper} data-testid="userInternshipPackInfo-progressSummaryBarWrapper">
                                <div className={Styles.progressSummaryBar} data-testid="userInternshipPackInfo-progressSummaryBar">
                                    <div
                                        className={Styles.progressSummaryBarFill}
                                        data-testid="userInternshipPackInfo-progressSummaryBarFill"
                                        style={{ width: `${calculateProgress()}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Certifications Section */}
                    {data.certifications.length > 0 && (<section className={Styles.section} data-testid="UserInternshipPackInfo-section">
                        <div className={Styles.sectionHeader} data-testid="UserInternshipPackInfo-sectionHeader">
                            <WorkspacePremiumOutlined className={Styles.sectionIcon} />
                            <h2 className={Styles.sectionTitle} data-testid="UserInternshipPackInfo-sectionTitle">
                                {props.t?.('InternshipPack.UserInternshipPackInfo.sections.certificates')}
                            </h2>
                        </div>

                        <div className={Styles.certificatesGrid} data-testid="UserInternshipPackInfo-certificatesGrid">
                            {data.certifications.map((cert, index) => (
                                <div
                                    key={cert.id}
                                    className={Styles.certCard}
                                    data-testid="UserInternshipPackInfo-certCard"
                                    onClick={() => handleNavigation('certificate', cert.id)}
                                >
                                    <div className={Styles.certTopRow} data-testid="UserInternshipPackInfo-certTopRow">
                                        <WorkspacePremiumOutlined className={Styles.certBadgeIcon} />
                                        <span data-testid="UserInternshipPackInfo-statusBadge"
                                            className={`${Styles.statusChip} ${resolveStatusChipClass(cert.status, Styles)}`}
                                        >
                                            {formatStatus(cert.status)}
                                        </span>
                                    </div>

                                    <h3 className={Styles.certTitle} data-testid="UserInternshipPackInfo-certTitle">{cert.name}</h3>
                                    <p className={Styles.certDescription} data-testid="UserInternshipPackInfo-certDescription">{cert.descr}</p>

                                    <div className={Styles.certMetaRow} data-testid="UserInternshipPackInfo-certMetaRow">
                                        <div className={Styles.certRequirements} data-testid="UserInternshipPackInfo-certRequirements">
                                            <CheckCircleOutlined className={Styles.requirementIcon} />
                                            <span className={Styles.requirementText} data-testid="UserInternshipPackInfo-requirementText">
                                                {props.t?.('InternshipPack.UserInternshipPackInfo.labels.courseRequirements')}
                                            </span>
                                        </div>
                                        <div className={Styles.completedRow} data-testid="UserInternshipPackInfo-completedRow">
                                            <span className={Styles.completedValue} data-testid="UserInternshipPackInfo-completedValue">{cert.completed_labs_count} / {cert.total_labs_count}</span>
                                        </div>
                                    </div>

                                    <div className={Styles.overallProgressRow} data-testid="UserInternshipPackInfo-overallProgressRow">
                                        <span className={Styles.overallLabel} data-testid="UserInternshipPackInfo-overallLabel">
                                            {props.t?.('InternshipPack.UserInternshipPackInfo.labels.overallProgress')}
                                        </span>
                                        <span className={Styles.overallPercent} data-testid="UserInternshipPackInfo-overallPercent">{cert.completion_percentage || 0}%</span>
                                    </div>

                                    <LinearProgress
                                        variant="determinate"
                                        value={cert.completion_percentage || 0}
                                        className={Styles.certProgressBar}
                                        data-testid="UserInternshipPackInfo-certProgressBar"
                                        sx={{
                                            height: '0.5em',
                                            borderRadius: '0.25em',
                                            backgroundColor: '#DEEBFD',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#1450A0',
                                                borderRadius: '0.25em'
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>)}

                    {/* Capstone Projects Section */}
                    {data.projects.length > 0 && (<section className={Styles.section} data-testid="projectsSection-section">
                        <div className={Styles.sectionHeader} data-testid="projectsSection-sectionHeader">
                            <LightbulbOutlined className={Styles.sectionIconprojects} />
                            <h2 className={Styles.sectionTitle} data-testid="projectsSection-sectionTitle">
                                {props.t?.('InternshipPack.UserInternshipPackInfo.sections.projects')}
                            </h2>
                        </div>

                        <div className={Styles.projectsGrid} data-testid="projectsSection-projectsGrid">
                            {data.projects.map((project) => {
                                const hasCert = !!project.cert_id;
                                return (<div
                                    key={project.id}
                                    className={Styles.projectCard}
                                    data-testid="projectsSection-projectCard"
                                    data-tooltip-id={(hasCert && !isDisabled) ? "purchase_labs-internship" : undefined}
                                    data-tooltip-content={
                                        hasCert
                                            ? JSON.stringify(labCerts) : undefined
                                    }
                                    onClick={() => {
                                        if (!hasCert) {
                                            handleNavigation('project', project.id, project);
                                        } else {
                                            labsCerts(project.id);
                                        }
                                    }}
                                >
                                    <div className={Styles.projectTopRow} data-testid="projectsSection-projectTopRow">
                                        <LightbulbOutlined className={Styles.projectIconSmall} />
                                        <span data-testid="projectsSection-statusBadge"
                                            className={`${Styles.statusChip} ${resolveStatusChipClass(project.status, Styles)}`}
                                        >
                                            {formatStatus(project.status)}
                                        </span>
                                    </div>

                                    <h3 className={Styles.projectTitle} data-testid="projectsSection-projectTitle">{project.name}</h3>
                                    <p className={Styles.projectDescription} data-testid="projectsSection-projectDescription">{project.descr}</p>

                                    <div className={Styles.progressSection} data-testid="projectsSection-progressSection">
                                        <span className={Styles.overallLabel} data-testid="projectsSection-progressLabel">
                                            {props.t?.('InternshipPack.UserInternshipPackInfo.labels.overallProgress')}
                                        </span>
                                        <div className={Styles.progressBarWrapper} data-testid="projectsSection-progressBarWrapper">
                                            <LinearProgress
                                                variant="determinate"
                                                value={project.completion_percentage || 0}
                                                className={Styles.courseProgressBar}
                                                data-testid="projectsSection-courseProgressBar"
                                                sx={{
                                                    height: '0.5em',
                                                    borderRadius: '0.25em',
                                                    backgroundColor: '#DEEBFD',
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: '#1450A0',
                                                        borderRadius: '0.25em'
                                                    }
                                                }}
                                            />
                                            <span className={Styles.progressPercent} data-testid="projectsSection-progressPercent">{project.completion_percentage || 0}%</span>
                                        </div>
                                    </div>
                                </div>)
                            })}
                        </div>
                    </section>)}

                    {/* Courses Section */}
                    {data.labs.length > 0 && (<section className={Styles.section} data-testid="coursesSection-section">
                        <div className={Styles.sectionHeader} data-testid="coursesSection-sectionHeader">
                            <MenuBookRounded className={Styles.sectionIcon} />
                            <h2 className={Styles.sectionTitle} data-testid="coursesSection-sectionTitle">
                                {props.t?.('InternshipPack.UserInternshipPackInfo.sections.courses')}
                            </h2>
                        </div>

                        <div className={Styles.coursesGrid} data-testid="coursesSection-coursesGrid">
                            {data.labs.map((course, index) => {
                                const hasCert = !!course.cert_id;
                                return (
                                    <div
                                        key={course.id}
                                        className={Styles.courseCard}
                                        data-testid="coursesSection-courseCard"
                                        data-tooltip-id={(hasCert && !isDisabled) ? "purchase_labs-internship" : undefined}
                                        data-tooltip-content={
                                            hasCert
                                                ? JSON.stringify(labCerts) : undefined
                                        }
                                        onClick={() => {
                                            if (!hasCert) {
                                                handleNavigation('course', course.id, course);
                                            } else {
                                                labsCerts(course.id);
                                            }
                                        }}
                                    >
                                        <div className={Styles.courseTopRow} data-testid="coursesSection-courseTopRow">
                                            <MenuBookRounded className={Styles.courseIconSmall} />
                                            <span data-testid="coursesSection-statusBadge"
                                                className={`${Styles.statusChip} ${resolveStatusChipClass(course.status, Styles)}`}
                                            >
                                                {formatStatus(course.status)}
                                            </span>
                                        </div>

                                        <h3 className={Styles.courseTitle} data-testid="coursesSection-courseTitle">{course.name}</h3>
                                        <p className={Styles.courseDescription} data-testid="coursesSection-courseDescription">{course.descr}</p>

                                        <div className={Styles.progressSection} data-testid="coursesSection-progressSection">
                                            <span className={Styles.overallLabel} data-testid="coursesSection-progressLabel">
                                                {props.t?.('InternshipPack.UserInternshipPackInfo.labels.overallProgress')}
                                            </span>
                                            <div className={Styles.progressBarWrapper} data-testid="coursesSection-progressBarWrapper">
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={course.completion_percentage || 0}
                                                    className={Styles.courseProgressBar}
                                                    data-testid="coursesSection-courseProgressBar"
                                                    sx={{
                                                        height: '0.5em',
                                                        borderRadius: '0.25em',
                                                        backgroundColor: '#DEEBFD',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: '#1450A0',
                                                            borderRadius: '0.25em'
                                                        }
                                                    }}
                                                />
                                                <span className={Styles.progressPercent} data-testid="coursesSection-progressPercent">{course.completion_percentage || 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </section>)}
                </div>
            </div>

            {/* Not Onboarded Dialog */}
            <Dialog
                open={showNotOnboardedDialog}
                onClose={() => setShowNotOnboardedDialog(false)}
                maxWidth="xs"
            >
                <div id="tcContainer" data-testid="userInternshipPackInfo-tcContainer" className={Styles.tcContainer} style={{ minWidth: 360, padding: 20 }}>
                    <h3 data-testid="userInternshipPackInfo-tcTitle" id="tcTitle" className={Styles.tcTitle}>
                        {props.t('LearningPath.UserLearningPath.profile.heading')}
                    </h3>
                    <p data-testid="userInternshipPackInfo-tcBody" id="tcBody" className={Styles.tcBody}>
                        {props.t('LearningPath.UserLearningPath.profile.message')}
                    </p>

                    <div data-testid="userInternshipPackInfo-tcActions" id="tcActions" className={Styles.tcActions}>
                        <AivagamButton
                            data-testid="userInternshipPackInfo-closeNotOnboarded"
                            id="closeNotOnboarded"
                            buttonText="Close"
                            onClick={() => setShowNotOnboardedDialog(false)}
                            type="cancel"
                        />
                        <AivagamButton
                            data-testid="userInternshipPackInfo-goToAccount"
                            id="goToAccount"
                            buttonText="Go to Account"
                            onClick={() => {
                                setShowNotOnboardedDialog(false);
                                navigate("/account");
                            }}
                            type="confirm"
                        />
                    </div>
                </div>
            </Dialog>

            <ReactTooltip
                id="purchase_labs-internship"
                globalCloseEvents={{ scroll: true }}
                clickable
                openOnClick
                place="right"
                opacity={1}
                style={{
                    backgroundColor: "#ffffff",
                    color: "#222",
                    maxWidth: "400px",
                    minWidth: "280px",
                    width: "auto",
                    zIndex: 99999,
                    border: "1px solid #d0d0d0",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
                    padding: "20px",
                    borderRadius: "12px",
                }}
                render={() => (
                    <div
                        data-testid="ProductGridCard-tooltipMain"
                        style={{
                            backgroundColor: "#ffffff",
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            width: "100%",
                            position: "relative",
                            zIndex: 99999,
                        }}
                    >
                        <p
                            style={{
                                margin: 0,
                                padding: "8px 0",
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "rgb(45, 48, 53)",
                                backgroundColor: "#ffffff",
                                lineHeight: "1.5",
                            }}
                        >
                            {viewContext.t("InternshipPack.UserInternshipPackInfo.prompt")}
                        </p>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            {labCerts?.map((cert, index) => (
                                <div
                                    data-testid="ProductListCard-tooltip-container"
                                    style={{
                                        width: '100%',
                                        backgroundColor: "#ffffff",
                                    }}
                                    key={index}
                                >
                                    <Button
                                        key={cert?.id}
                                        data-testid={`ProductGridCard-tooltip-item-${index}-name`}
                                        onClick={() => handleNavigation('certificate', cert?.id)}
                                        disabled={cert?.isExpired}
                                        fullWidth
                                        variant="outlined"
                                        sx={{
                                            textTransform: "none",
                                            justifyContent: "flex-start",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            backgroundColor: "#ffffff",
                                            borderColor: cert?.isExpired ? "#e0e0e0" : "#1450A0",
                                            color: cert?.isExpired ? "#999" : "#1450A0",
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                            textAlign: "left",
                                            lineHeight: "1.5",
                                            minHeight: "44px",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                borderColor: cert?.isExpired ? "#e0e0e0" : "#0d3a75",
                                                backgroundColor: cert?.isExpired ? "#ffffff" : "rgba(20, 80, 160, 0.08)",
                                                transform: cert?.isExpired ? "none" : "translateX(4px)",
                                            },
                                            "&.Mui-disabled": {
                                                borderColor: "#e0e0e0",
                                                color: "#999",
                                                backgroundColor: "#f5f5f5",
                                                opacity: 0.6,
                                            },
                                        }}
                                    >
                                        {cert?.name}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            />
        </>
    );
}